import React, { useEffect, useState, useCallback } from 'react';
import { superAdminApi } from '@/services/superAdminApi';
import { useAuth } from '@/contexts/AuthContext';
import {
  Inbox, UserPlus, AlertTriangle, Archive, RefreshCw, Loader2, Send,
  StickyNote, Settings, Mail, Circle, X, Save, Clock, Bell, UserCircle2,
} from 'lucide-react';
import { toast } from 'sonner';

const FOLDERS = [
  { key: 'inbox', label: 'Posteingang', icon: Inbox },
  { key: 'leads', label: 'Anfragen / Leads', icon: UserPlus },
  { key: 'errors', label: 'Systemfehler', icon: AlertTriangle },
  { key: 'archive', label: 'Archiv', icon: Archive },
];

const STATUS_LABELS = { open: 'Offen', pending: 'In Arbeit', waiting: 'Wartet', closed: 'Geschlossen' };
const STATUS_COLORS = {
  open: 'bg-emerald-500/15 text-emerald-700',
  pending: 'bg-amber-500/15 text-amber-700',
  waiting: 'bg-sky-500/15 text-sky-700',
  closed: 'bg-slate-200 text-slate-600',
};
const PRIORITY_COLORS = {
  low: 'text-slate-500', normal: 'text-slate-600', high: 'text-amber-600', urgent: 'text-rose-600',
};
const SOURCE_LABELS = { email: 'E-Mail', lead: 'Anfrage', system_error: 'Systemfehler' };

function timeAgo(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return 'gerade eben';
  if (diff < 3600) return `vor ${Math.floor(diff / 60)} Min`;
  if (diff < 86400) return `vor ${Math.floor(diff / 3600)} Std`;
  return d.toLocaleDateString('de-CH', { day: '2-digit', month: '2-digit', year: '2-digit' });
}

export default function InboxPage() {
  const { user } = useAuth();
  const [folder, setFolder] = useState('inbox');
  const [overdueOnly, setOverdueOnly] = useState(false);
  const [cases, setCases] = useState([]);
  const [stats, setStats] = useState({});
  const [team, setTeam] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [detail, setDetail] = useState(null);
  const [reply, setReply] = useState('');
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [polling, setPolling] = useState(false);
  const [showConfig, setShowConfig] = useState(false);

  const loadStats = useCallback(async () => {
    try { const { data } = await superAdminApi.inboxStats(); setStats(data.counts || {}); } catch (e) {}
  }, []);

  const loadCases = useCallback(async (f, overdue) => {
    setLoading(true);
    try {
      const params = overdue ? { overdue: true } : { folder: f };
      const { data } = await superAdminApi.inboxCases(params);
      setCases(data.cases || []);
    } catch (e) { toast.error('Fälle konnten nicht geladen werden'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { loadStats(); superAdminApi.inboxTeam().then(({ data }) => setTeam(data.members || [])).catch(() => {}); }, [loadStats]);
  useEffect(() => { loadCases(folder, overdueOnly); setSelected(null); setDetail(null); }, [folder, overdueOnly, loadCases]);

  const openCase = async (c) => {
    setSelected(c);
    setDetail(null);
    try {
      const { data } = await superAdminApi.inboxCase(c.id);
      setDetail(data);
      if (c.unread) { loadStats(); setCases((prev) => prev.map((x) => x.id === c.id ? { ...x, unread: false } : x)); }
    } catch (e) { toast.error('Fall konnte nicht geladen werden'); }
  };

  const refreshDetail = async (id) => {
    try { const { data } = await superAdminApi.inboxCase(id); setDetail(data); } catch (e) {}
  };

  const patch = async (data) => {
    if (!selected) return;
    try {
      const { data: updated } = await superAdminApi.inboxPatchCase(selected.id, data);
      setSelected({ ...updated, overdue: selected.overdue });
      setDetail((d) => d ? { ...d, case: { ...updated, overdue: d.case?.overdue } } : d);
      loadCases(folder, overdueOnly);
      loadStats();
      toast.success('Aktualisiert');
    } catch (e) { toast.error(e.response?.data?.detail || 'Aktualisierung fehlgeschlagen'); }
  };

  const assign = async (memberId) => {
    if (!memberId) { await patch({ assignee_id: '', assignee_name: '' }); return; }
    const m = team.find((x) => x.id === memberId);
    const name = m ? (`${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email) : '';
    await patch({ assignee_id: memberId, assignee_name: name });
  };

  const sendReply = async () => {
    if (!reply.trim() || !selected) return;
    setSending(true);
    try {
      await superAdminApi.inboxReply(selected.id, reply.trim());
      setReply('');
      await refreshDetail(selected.id);
      toast.success('Antwort gesendet');
    } catch (e) { toast.error(e.response?.data?.detail || 'Antwort fehlgeschlagen'); }
    finally { setSending(false); }
  };

  const sendNote = async () => {
    if (!note.trim() || !selected) return;
    setSending(true);
    try {
      await superAdminApi.inboxNote(selected.id, note.trim());
      setNote('');
      await refreshDetail(selected.id);
      toast.success('Notiz gespeichert');
    } catch (e) { toast.error('Notiz fehlgeschlagen'); }
    finally { setSending(false); }
  };

  const pollNow = async () => {
    setPolling(true);
    try {
      const { data } = await superAdminApi.inboxPoll();
      if (data.skipped) toast.message('Posteingang noch nicht konfiguriert', { description: 'Bitte zuerst die Mailbox hinterlegen.' });
      else if (data.error) toast.error(`Abruf-Fehler: ${data.error}`);
      else toast.success(`${data.created || 0} neue Nachricht(en) abgerufen`);
      loadCases(folder, overdueOnly); loadStats();
    } catch (e) { toast.error('Abruf fehlgeschlagen'); }
    finally { setPolling(false); }
  };

  return (
    <div data-testid="sa-inbox" className="space-y-6">
      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold tracking-tight text-slate-900">Support-Posteingang</h1>
          <p className="mt-1 text-sm text-slate-600">
            Zentraler Posteingang des StrongFit-Teams. Fälle entstehen aus E-Mails, Kontakt-/Lead-Anfragen und Systemfehlern.
          </p>
        </div>
        <div className="flex shrink-0 items-center gap-2">
          <button onClick={pollNow} disabled={polling} data-testid="sa-inbox-poll-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-100 disabled:opacity-50">
            {polling ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <RefreshCw className="h-3.5 w-3.5" />}
            Jetzt abrufen
          </button>
          <button onClick={() => setShowConfig(true)} data-testid="sa-inbox-config-btn"
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-xs font-semibold text-slate-800 hover:bg-slate-100">
            <Settings className="h-3.5 w-3.5" /> Mailbox
          </button>
        </div>
      </div>

      <div className="grid grid-cols-1 gap-4 lg:grid-cols-[220px_340px_1fr]">
        {/* Ordner */}
        <div className="space-y-1" data-testid="sa-inbox-folders">
          {FOLDERS.map((f) => {
            const Icon = f.icon;
            const count = stats[f.key] || 0;
            return (
              <button key={f.key} onClick={() => { setOverdueOnly(false); setFolder(f.key); }} data-testid={`sa-inbox-folder-${f.key}`}
                className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${!overdueOnly && folder === f.key ? 'bg-indigo-500/15 text-slate-900' : 'text-slate-600 hover:bg-slate-100'}`}>
                <span className="flex items-center gap-2"><Icon className="h-4 w-4" /> {f.label}</span>
                {count > 0 && <span className="rounded-full bg-slate-200 px-2 py-0.5 text-[10px] font-semibold text-slate-800">{count}</span>}
              </button>
            );
          })}
          <div className="my-2 h-px bg-slate-100" />
          <button onClick={() => setOverdueOnly(true)} data-testid="sa-inbox-filter-overdue"
            className={`flex w-full items-center justify-between rounded-lg px-3 py-2 text-sm transition-colors ${overdueOnly ? 'bg-rose-500/15 text-rose-700' : 'text-slate-600 hover:bg-slate-100'}`}>
            <span className="flex items-center gap-2"><Clock className="h-4 w-4" /> Überfällig</span>
            {(stats.overdue || 0) > 0 && <span className="rounded-full bg-rose-500/80 px-2 py-0.5 text-[10px] font-semibold text-slate-900">{stats.overdue}</span>}
          </button>
        </div>

        {/* Fallliste */}
        <div className="space-y-2" data-testid="sa-inbox-case-list">
          {loading ? (
            <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
          ) : cases.length === 0 ? (
            <div className="flex flex-col items-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 py-16 text-slate-500">
              <Mail className="h-8 w-8" /><span className="text-sm">Keine Fälle in diesem Ordner.</span>
            </div>
          ) : cases.map((c) => (
            <button key={c.id} onClick={() => openCase(c)} data-testid="sa-inbox-case-row"
              className={`w-full rounded-xl border px-4 py-3 text-left transition-colors ${selected?.id === c.id ? 'border-indigo-500/60 bg-indigo-500/10' : 'border-slate-200 bg-white hover:bg-slate-100'}`}>
              <div className="flex items-center gap-2">
                {c.unread && <Circle className="h-2 w-2 shrink-0 fill-indigo-400 text-indigo-600" />}
                <span className={`truncate text-sm ${c.unread ? 'font-semibold text-slate-900' : 'font-medium text-slate-800'}`}>{c.subject}</span>
              </div>
              <div className="mt-1 flex items-center gap-2">
                <span className="truncate text-xs text-slate-500">{c.requester_email || SOURCE_LABELS[c.source] || c.source}</span>
              </div>
              <div className="mt-2 flex items-center gap-2">
                <span className={`rounded px-1.5 py-0.5 text-[10px] font-semibold ${STATUS_COLORS[c.status]}`}>{STATUS_LABELS[c.status]}</span>
                {c.overdue && <span className="flex items-center gap-1 rounded bg-rose-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-rose-700" data-testid="sa-inbox-overdue-badge"><Clock className="h-2.5 w-2.5" /> überfällig</span>}
                {c.assignee_name && <span className="flex items-center gap-1 rounded bg-sky-500/15 px-1.5 py-0.5 text-[10px] font-semibold text-sky-700" data-testid="sa-inbox-assignee-badge"><UserCircle2 className="h-2.5 w-2.5" /> {c.assignee_name.split(' ')[0]}</span>}
                <span className="ml-auto text-[10px] text-slate-500">{timeAgo(c.last_message_at)}</span>
              </div>
            </button>
          ))}
        </div>

        {/* Detail */}
        <div data-testid="sa-inbox-detail">
          {!selected ? (
            <div className="flex h-full min-h-[300px] flex-col items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-slate-50 text-slate-500">
              <Mail className="h-8 w-8" /><span className="text-sm">Wähle einen Fall aus.</span>
            </div>
          ) : (
            <div className="rounded-2xl border border-slate-200 bg-white p-5">
              <div className="flex items-start justify-between gap-4">
                <div className="min-w-0">
                  <h2 className="truncate text-base font-semibold text-slate-900">{selected.subject}</h2>
                  <p className="mt-0.5 text-xs text-slate-500">
                    {selected.requester_name ? `${selected.requester_name} · ` : ''}{selected.requester_email || '—'}
                  </p>
                  {selected.link_type && (
                    <span className="mt-1 inline-block rounded bg-slate-100 px-1.5 py-0.5 text-[10px] font-semibold text-slate-700">
                      Verknüpft: {selected.link_type}
                    </span>
                  )}
                </div>
                {selected.overdue && (
                  <span className="flex shrink-0 items-center gap-1 rounded-lg bg-rose-500/15 px-2 py-1 text-[11px] font-semibold text-rose-700" data-testid="sa-inbox-detail-overdue">
                    <Clock className="h-3.5 w-3.5" /> Überfällig
                  </span>
                )}
              </div>

              {/* Zuweisung */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <span className="flex items-center gap-1.5 text-xs text-slate-600"><UserCircle2 className="h-4 w-4" /> Zuständig:</span>
                <select value={selected.assignee_id || ''} onChange={(e) => assign(e.target.value)}
                  data-testid="sa-inbox-assignee-select"
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none">
                  <option value="">— Nicht zugewiesen —</option>
                  {team.map((m) => {
                    const nm = `${m.first_name || ''} ${m.last_name || ''}`.trim() || m.email;
                    return <option key={m.id} value={m.id}>{nm}</option>;
                  })}
                </select>
                {user && selected.assignee_id !== user.id && (
                  <button onClick={() => assign(user.id)} data-testid="sa-inbox-assign-me"
                    className="rounded-lg border border-slate-200 bg-slate-100 px-2 py-1.5 text-xs font-semibold text-slate-800 hover:bg-slate-200">
                    Mir zuweisen
                  </button>
                )}
              </div>

              {/* Steuerung */}
              <div className="mt-4 flex flex-wrap items-center gap-2">
                <select value={selected.status} onChange={(e) => patch({ status: e.target.value })}
                  data-testid="sa-inbox-status-select"
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none">
                  {Object.entries(STATUS_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
                </select>
                <select value={selected.priority} onChange={(e) => patch({ priority: e.target.value })}
                  data-testid="sa-inbox-priority-select"
                  className={`rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs focus:border-indigo-500 focus:outline-none ${PRIORITY_COLORS[selected.priority]}`}>
                  <option value="low">Niedrig</option><option value="normal">Normal</option>
                  <option value="high">Hoch</option><option value="urgent">Dringend</option>
                </select>
                <select value={selected.folder} onChange={(e) => patch({ folder: e.target.value })}
                  data-testid="sa-inbox-folder-select"
                  className="rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-xs text-slate-800 focus:border-indigo-500 focus:outline-none">
                  {FOLDERS.map((f) => <option key={f.key} value={f.key}>{f.label}</option>)}
                </select>
              </div>

              {/* Verlauf */}
              <div className="mt-5 space-y-3 max-h-[380px] overflow-y-auto pr-1" data-testid="sa-inbox-thread">
                {!detail ? (
                  <div className="flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
                ) : detail.messages.map((m) => {
                  const isOut = m.direction === 'outbound';
                  const isNote = m.direction === 'note';
                  const isSys = m.direction === 'system';
                  return (
                    <div key={m.id} className={`rounded-xl border p-3 text-sm ${
                      isNote ? 'border-amber-200 bg-amber-500/5' :
                      isSys ? 'border-rose-200 bg-rose-500/5' :
                      isOut ? 'border-indigo-200 bg-indigo-500/10 ml-6' : 'border-slate-200 bg-white mr-6'}`}>
                      <div className="mb-1 flex items-center gap-2 text-[10px] uppercase tracking-wide text-slate-500">
                        {isNote ? <><StickyNote className="h-3 w-3" /> Interne Notiz</> :
                         isSys ? <><AlertTriangle className="h-3 w-3" /> System</> :
                         isOut ? <><Send className="h-3 w-3" /> Antwort</> : <><Mail className="h-3 w-3" /> Eingegangen</>}
                        <span className="ml-auto normal-case">{timeAgo(m.received_at)}</span>
                      </div>
                      <pre className="whitespace-pre-wrap break-words font-sans text-slate-800">{m.text_body || '(kein Textinhalt)'}</pre>
                      {m.attachments?.length > 0 && (
                        <div className="mt-2 text-[10px] text-slate-500">📎 {m.attachments.map((a) => a.filename).join(', ')}</div>
                      )}
                    </div>
                  );
                })}
              </div>

              {/* Antwort */}
              <div className="mt-4 space-y-2">
                <textarea value={reply} onChange={(e) => setReply(e.target.value)} rows={3}
                  placeholder="Antwort an den Absender…" data-testid="sa-inbox-reply-input"
                  className="w-full rounded-xl border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-500 focus:border-indigo-500 focus:outline-none" />
                <div className="flex items-center justify-between gap-2">
                  <button onClick={sendReply} disabled={sending || !reply.trim()} data-testid="sa-inbox-reply-btn"
                    className="inline-flex items-center gap-2 rounded-lg bg-indigo-500 px-3 py-2 text-xs font-semibold text-white hover:bg-indigo-400 disabled:opacity-50">
                    {sending ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Send className="h-3.5 w-3.5" />} Antwort senden
                  </button>
                </div>
                <div className="flex items-center gap-2">
                  <input value={note} onChange={(e) => setNote(e.target.value)}
                    placeholder="Interne Notiz hinzufügen…" data-testid="sa-inbox-note-input"
                    className="flex-1 rounded-lg border border-slate-200 bg-slate-50 px-3 py-2 text-xs text-slate-800 placeholder:text-slate-400 focus:border-amber-500/50 focus:outline-none" />
                  <button onClick={sendNote} disabled={sending || !note.trim()} data-testid="sa-inbox-note-btn"
                    className="inline-flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-500/10 px-3 py-2 text-xs font-semibold text-amber-700 hover:bg-amber-500/20 disabled:opacity-50">
                    <StickyNote className="h-3.5 w-3.5" /> Notiz
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {showConfig && <ConfigDialog onClose={() => { setShowConfig(false); loadStats(); }} />}
    </div>
  );
}

function ConfigDialog({ onClose }) {
  const [cfg, setCfg] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await superAdminApi.inboxConfig();
        setCfg({ ...data, imap_password: '', smtp_password: '' });
      } catch (e) { toast.error('Konfiguration konnte nicht geladen werden'); }
    })();
  }, []);

  const save = async () => {
    setSaving(true);
    try {
      const payload = { ...cfg };
      if (!payload.imap_password) delete payload.imap_password;
      if (!payload.smtp_password) delete payload.smtp_password;
      await superAdminApi.inboxSaveConfig(payload);
      toast.success('Mailbox-Konfiguration gespeichert');
      onClose();
    } catch (e) { toast.error('Speichern fehlgeschlagen'); }
    finally { setSaving(false); }
  };

  const field = (label, key, type = 'text', placeholder = '') => (
    <label className="block">
      <span className="text-xs text-slate-600">{label}</span>
      <input type={type} value={cfg[key] ?? ''} placeholder={placeholder}
        onChange={(e) => setCfg({ ...cfg, [key]: e.target.value })}
        data-testid={`sa-inbox-cfg-${key}`}
        className="mt-1 w-full rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm text-slate-900 placeholder:text-slate-400 focus:border-indigo-500 focus:outline-none" />
    </label>
  );

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center bg-black/60 p-4" data-testid="sa-inbox-config-dialog">
      <div className="max-h-[90vh] w-full max-w-lg overflow-y-auto rounded-2xl border border-slate-200 bg-white p-6">
        <div className="flex items-center justify-between">
          <h3 className="text-base font-semibold text-slate-900">Support-Mailbox</h3>
          <button onClick={onClose} className="text-slate-500 hover:text-slate-900" data-testid="sa-inbox-cfg-close"><X className="h-5 w-5" /></button>
        </div>
        {!cfg ? (
          <div className="mt-6 flex items-center gap-2 text-slate-600"><Loader2 className="h-4 w-4 animate-spin" /> Lädt…</div>
        ) : (
          <div className="mt-4 space-y-4">
            <label className="flex items-center gap-2">
              <input type="checkbox" checked={!!cfg.enabled} onChange={(e) => setCfg({ ...cfg, enabled: e.target.checked })}
                data-testid="sa-inbox-cfg-enabled" className="h-4 w-4 rounded border-slate-300 bg-slate-100" />
              <span className="text-sm text-slate-800">Echten E-Mail-Empfang aktivieren (IMAP-Abruf alle 2 Min)</span>
            </label>

            <div className="rounded-xl border border-slate-200 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Empfang (IMAP)</p>
              <div className="grid grid-cols-2 gap-3">
                {field('IMAP-Host', 'imap_host', 'text', 'imap.example.com')}
                {field('Port', 'imap_port', 'number')}
                {field('Benutzer', 'imap_username', 'text', 'support@strongfit.ch')}
                {field('Ordner', 'imap_folder', 'text', 'INBOX')}
              </div>
              {field(cfg.imap_password_set ? 'Passwort (gesetzt – zum Ändern neu eingeben)' : 'Passwort / App-Passwort', 'imap_password', 'password', cfg.imap_password_set ? '••••••••' : '')}
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-500">Versand (SMTP, für Antworten)</p>
              <div className="grid grid-cols-2 gap-3">
                {field('SMTP-Host', 'smtp_host', 'text', 'smtp.example.com')}
                {field('Port', 'smtp_port', 'number')}
                {field('Benutzer', 'smtp_username', 'text', 'support@strongfit.ch')}
                {field('Absenderadresse', 'support_address', 'text', 'support@strongfit.ch')}
              </div>
              {field(cfg.smtp_password_set ? 'Passwort (gesetzt – zum Ändern neu eingeben)' : 'Passwort / App-Passwort', 'smtp_password', 'password', cfg.smtp_password_set ? '••••••••' : '')}
            </div>

            <div className="rounded-xl border border-slate-200 p-3">
              <p className="mb-2 flex items-center gap-1.5 text-xs font-semibold uppercase tracking-wide text-slate-500"><Bell className="h-3.5 w-3.5" /> SLA & Erinnerungen</p>
              <label className="flex items-center gap-2">
                <input type="checkbox" checked={!!cfg.reminders_enabled} onChange={(e) => setCfg({ ...cfg, reminders_enabled: e.target.checked })}
                  data-testid="sa-inbox-cfg-reminders" className="h-4 w-4 rounded border-slate-300 bg-slate-100" />
                <span className="text-sm text-slate-800">Team stündlich an überfällige Fälle erinnern (E-Mail an alle Super-Admins)</span>
              </label>
              <div className="mt-3 flex items-center gap-2">
                <span className="text-xs text-slate-600">Als überfällig markieren nach</span>
                <input type="number" min="1" value={cfg.sla_hours ?? 24}
                  onChange={(e) => setCfg({ ...cfg, sla_hours: e.target.value })}
                  data-testid="sa-inbox-cfg-sla-hours"
                  className="w-20 rounded-lg border border-slate-200 bg-white px-2 py-1.5 text-sm text-slate-900 focus:border-indigo-500 focus:outline-none" />
                <span className="text-xs text-slate-600">Stunden ohne Antwort</span>
              </div>
              {cfg.last_reminder_at && (
                <p className="mt-2 text-[11px] text-slate-500">Letzte Prüfung: {timeAgo(cfg.last_reminder_at)} · {cfg.last_reminder_status}</p>
              )}
            </div>

            {cfg.last_poll_at && (
              <p className="text-[11px] text-slate-500">Letzter Abruf: {timeAgo(cfg.last_poll_at)} · {cfg.last_poll_status}</p>
            )}

            <button onClick={save} disabled={saving} data-testid="sa-inbox-cfg-save"
              className="inline-flex w-full items-center justify-center gap-2 rounded-lg bg-indigo-500 px-3 py-2.5 text-sm font-semibold text-white hover:bg-indigo-400 disabled:opacity-50">
              {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />} Speichern
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
